/**
 * 浏览器兼容性检测工具
 * 用于检测浏览器是否支持特定功能，并提供回退方案
 */

/**
 * 检测浏览器类型和版本
 * @returns {Object} 浏览器兼容性信息
 */
export const detectBrowser = () => {
  const ua = navigator.userAgent;
  let browser = 'unknown';
  let version = 'unknown';
  let isOldBrowser = false;

  // 检测IE
  const msie = ua.indexOf('MSIE ');
  if (msie > 0) {
    browser = 'IE';
    version = parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10);
    isOldBrowser = true;
  }

  // 检测IE 11
  const trident = ua.indexOf('Trident/');
  if (trident > 0) {
    browser = 'IE';
    const rv = ua.indexOf('rv:');
    version = parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10);
    isOldBrowser = true;
  }

  // 检测Edge (旧版)
  const edge = ua.indexOf('Edge/');
  if (edge > 0) {
    browser = 'Edge';
    version = parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
    isOldBrowser = version < 16;
  }

  // 检测Chrome
  const chrome = ua.indexOf('Chrome/');
  if (chrome > 0 && ua.indexOf('Edge/') === -1) {
    browser = 'Chrome';
    version = parseInt(ua.substring(chrome + 7, ua.indexOf('.', chrome)), 10);
    isOldBrowser = version < 60;
  }

  // 检测Firefox
  const firefox = ua.indexOf('Firefox/');
  if (firefox > 0) {
    browser = 'Firefox';
    version = parseInt(ua.substring(firefox + 8, ua.indexOf('.', firefox)), 10);
    isOldBrowser = version < 55;
  }

  // 检测Safari
  const safari = ua.indexOf('Safari/');
  if (safari > 0 && ua.indexOf('Chrome') === -1) {
    browser = 'Safari';
    const versionStr = ua.substring(ua.indexOf('Version/') + 8, ua.indexOf(' Safari'));
    version = parseInt(versionStr, 10);
    isOldBrowser = version < 10;
  }

  return {
    browser,
    version,
    isOldBrowser,
    isIE: browser === 'IE',
    isEdge: browser === 'Edge',
    isChrome: browser === 'Chrome',
    isFirefox: browser === 'Firefox',
    isSafari: browser === 'Safari'
  };
};

/**
 * 检测浏览器是否支持特定CSS功能
 * @returns {Object} CSS功能支持信息
 */
export const detectCSSSupport = () => {
  const supportsFlexbox = typeof document !== 'undefined' && 
    ('flexBasis' in document.documentElement.style || 
     'webkitFlexBasis' in document.documentElement.style || 
     'mozFlexBasis' in document.documentElement.style);
  
  const supportsGrid = typeof document !== 'undefined' && 
    ('grid' in document.documentElement.style || 
     'msGrid' in document.documentElement.style || 
     'webkitGrid' in document.documentElement.style);
  
  const supportsCSSVariables = typeof document !== 'undefined' && 
    (window.CSS && window.CSS.supports && window.CSS.supports('--a', '0'));
  
  return {
    supportsFlexbox,
    supportsGrid,
    supportsCSSVariables
  };
};

/**
 * 检测浏览器是否支持特定JavaScript功能
 * @returns {Object} JavaScript功能支持信息
 */
export const detectJSSupport = () => {
  const supportsPromises = typeof Promise !== 'undefined';
  const supportsAsync = typeof Object.getOwnPropertyDescriptor(Function.prototype, 'asyncIterator') !== 'undefined';
  const supportsLocalStorage = typeof localStorage !== 'undefined';
  const supportsSessionStorage = typeof sessionStorage !== 'undefined';
  
  return {
    supportsPromises,
    supportsAsync,
    supportsLocalStorage,
    supportsSessionStorage
  };
};

/**
 * 获取完整的浏览器兼容性信息
 * @returns {Object} 完整的浏览器兼容性信息
 */
export const getBrowserCompatibility = () => {
  const browserInfo = detectBrowser();
  const cssSupport = detectCSSSupport();
  const jsSupport = detectJSSupport();
  
  return {
    ...browserInfo,
    ...cssSupport,
    ...jsSupport
  };
};

/**
 * 应用CSS兼容性修复
 * 为不支持现代CSS功能的浏览器添加兼容性类
 */
export const applyCompatibilityFixes = () => {
  if (typeof document === 'undefined') return;
  
  const compat = getBrowserCompatibility();
  const html = document.documentElement;
  
  // 添加浏览器标识类
  html.classList.add(`browser-${compat.browser.toLowerCase()}`);
  html.classList.add(`browser-version-${compat.version}`);
  
  if (compat.isOldBrowser) {
    html.classList.add('old-browser');
  }
  
  // 添加功能支持类
  if (!compat.supportsFlexbox) {
    html.classList.add('no-flexbox');
  }
  
  if (!compat.supportsGrid) {
    html.classList.add('no-grid');
  }
  
  if (!compat.supportsCSSVariables) {
    html.classList.add('no-css-variables');
  }
  
  // 将兼容性信息添加到window对象
  window.browserCompat = compat;
  
  return compat;
};

export default {
  detectBrowser,
  detectCSSSupport,
  detectJSSupport,
  getBrowserCompatibility,
  applyCompatibilityFixes
}; 